import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Row, Col, Form, Button, Spinner } from "react-bootstrap";
import 'react-phone-number-input/style.css';
import { updateUser } from "../../../redux/slices/userSlice";
import { getCurrentUser } from "../../../redux/slices/authSlice";
import { parseDateOnly } from "../../../utils/userValidators";
import PhoneInput, { isPossiblePhoneNumber } from 'react-phone-number-input';
import { toast } from "react-toastify";

const getErrorMessage = (error, fallback) => {
  const detail = error?.payload?.detail || error?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        const field = Array.isArray(item?.loc) ? item.loc[item.loc.length - 1] : null;
        return field ? `${field}: ${item.msg || item.message || ""}` : item.msg || item.message || "";
      })
      .filter(Boolean)
      .join(" | ");
  }
  if (detail && typeof detail === "object") {
    return detail.msg || detail.message || JSON.stringify(detail);
  }
  return error?.message || fallback;
};

const SELF_EDITABLE_FIELDS = new Set([
    "first_name",
    "last_name",
    "phone_number",
    "date_of_birth",
    "emergency_contact_phone",
    "address",
    "city",
    "postal_code",
    "country",
    "region",
]);

const NATIONALITIES = [
    "Alemã", "Americana", "Angolana", "Brasileira", "Britânica",
    "Cabo-verdiana", "Espanhola", "Francesa", "Guineense", "Italiana",
    "Moçambicana", "Portuguesa", "Santomense", "Suíça", "Timorense", "Outra"
];

const COUNTRIES = [
    "Alemanha", "Angola", "Brasil", "Cabo Verde", "Espanha",
    "Estados Unidos", "França", "Guiné-Bissau", "Itália", "Moçambique",
    "Portugal", "Reino Unido", "São Tomé e Príncipe", "Suíça", "Timor-Leste", "Outro"
];

// Same limits as the backend (userSchema.validate_date_of_birth), plus the adult check.
const MIN_AGE_YEARS = 18;
const MAX_AGE_YEARS = 120;

const PersonalArea = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const currentUser = useSelector((state) => state.auth.user);

    const [formData, setFormData] = useState({});
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (currentUser) {
            setFormData({
                first_name: currentUser.first_name || "",
                last_name: currentUser.last_name || "",
                phone_number: currentUser.phone_number ? currentUser.phone_number.replace(/\s+/g, '') : "",
                emergency_contact_phone: currentUser.emergency_contact_phone ? currentUser.emergency_contact_phone.replace(/\s+/g, '') : "",
                date_of_birth: currentUser.date_of_birth ? currentUser.date_of_birth.split('T')[0] : "",
                nationality: currentUser.nationality || "",
                iban: currentUser.iban || "",
                swift_bic: currentUser.swift_bic || "",
                cc: currentUser.cc || "",
                social_security_number: currentUser.social_security_number || "",
                nif: currentUser.nif || "",
                address: currentUser.address || "",
                city: currentUser.city || "",
                postal_code: currentUser.postal_code || "",
                country: currentUser.country || "",
                region: currentUser.region || "",
            });
        }
    }, [currentUser]);

    // Phone and date of birth are optional
    const getPhoneError = (phone) => {
        if (!phone || isPossiblePhoneNumber(phone)) return null;
        return t('components.PersonalArea.errors.invalid_phone', 'Número de telefone inválido.');
    };

    const getDateOfBirthError = (birthDate) => {
        if (!birthDate) return null;

        const dob = parseDateOnly(birthDate);
        const today = new Date();
        if (!dob || dob.getFullYear() < today.getFullYear() - MAX_AGE_YEARS) {
            return t('components.PersonalArea.errors.invalid_dob', 'Data de nascimento inválida.');
        }
        if (dob > today) {
            return t('components.PersonalArea.errors.future_dob', 'A data de nascimento não pode ser no futuro.');
        }

        let age = today.getFullYear() - dob.getFullYear();
        const monthDiff = today.getMonth() - dob.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) age--;
        if (age < MIN_AGE_YEARS) {
            return t('components.PersonalArea.errors.underage_dob', 'Deve ter pelo menos 18 anos.');
        }
        return null;
    };

    const phoneError = getPhoneError(formData.phone_number);
    const dateOfBirthError = getDateOfBirthError(formData.date_of_birth);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handlePhoneChange = (value, fieldName) => {
        const cleanedValue = value ? value.replace(/\s+/g, '') : "";
        setFormData((prev) => ({ ...prev, [fieldName]: cleanedValue }));
    };

    const handleSave = async (e) => {
        if (e) e.preventDefault();
        if (phoneError || dateOfBirthError) return;

        setIsSaving(true);
        const updates = {};
        Object.entries(formData).forEach(([key, value]) => {
            if (!SELF_EDITABLE_FIELDS.has(key)) return;
            updates[key] = typeof value === "string" ? value.trim() : value;
        });
        if (!updates.phone_number) updates.phone_number = null;
        if (!updates.date_of_birth) updates.date_of_birth = null;

        try {
            await dispatch(updateUser({ userId: currentUser.id, updates })).unwrap();
            dispatch(getCurrentUser());
        } catch (error) {
            toast.error(
                getErrorMessage(
                    error,
                    t('components.PersonalArea.save_error', {
                        defaultValue: 'Não foi possível guardar as alterações.',
                    })
                )
            );
        } finally {
            setIsSaving(false);
        }
    };

    if (!currentUser) return null;

    return (
        <div className="pt-3">
            <div className="mb-5">
                <h3 className="txt-main fw-bold">{t('components.PersonalArea.general_info')}</h3>
                <p className="txt-subtle mb-4" style={{ fontSize: '0.9rem' }}>{t('components.PersonalArea.general_info_desc')}</p>
                <Form>
                    <Row className="mb-3 g-4">
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label className="txt-subtle fw-semibold" style={{ fontSize: '0.85rem' }}>{t('components.PersonalArea.first_name')}</Form.Label>
                                <Form.Control type="text" name="first_name" value={formData.first_name || ""} onChange={handleChange} className="bg-surface txt-main" />
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label className="txt-subtle fw-semibold" style={{ fontSize: '0.85rem' }}>{t('components.PersonalArea.last_name')}</Form.Label>
                                <Form.Control type="text" name="last_name" value={formData.last_name || ""} onChange={handleChange} className="bg-surface txt-main" />
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label className="txt-subtle fw-semibold" style={{ fontSize: '0.85rem' }}>{t('components.PersonalArea.phone')}</Form.Label>
                                <PhoneInput
                                    international
                                    defaultCountry="PT"
                                    value={formData.phone_number || ""}
                                    onChange={(val) => handlePhoneChange(val, 'phone_number')}
                                    className={`form-control bg-surface txt-main${phoneError ? " is-invalid" : ""}`}
                                    numberInputProps={{
                                        className: "border-0",
                                        "aria-invalid": !!phoneError,
                                        "aria-describedby": phoneError ? "personal-area-phone-error" : undefined,
                                    }}
                                />
                                {phoneError && (
                                    <div id="personal-area-phone-error" className="invalid-feedback d-block">
                                        {phoneError}
                                    </div>
                                )}
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row className="mb-3 g-4">
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label className="txt-subtle fw-semibold" style={{ fontSize: '0.85rem' }}>{t('components.PersonalArea.dob')}</Form.Label>
                                <Form.Control
                                    type="date"
                                    name="date_of_birth"
                                    value={formData.date_of_birth || ""}
                                    onChange={handleChange}
                                    className="bg-surface txt-main"
                                    isInvalid={!!dateOfBirthError}
                                    aria-describedby={dateOfBirthError ? "personal-area-dob-error" : undefined}
                                />
                                {dateOfBirthError && (
                                    <div id="personal-area-dob-error" className="invalid-feedback d-block">
                                        {dateOfBirthError}
                                    </div>
                                )}
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label className="txt-subtle fw-semibold" style={{ fontSize: '0.85rem' }}>{t('components.PersonalArea.nationality')}</Form.Label>
                                <Form.Select name="nationality" value={formData.nationality || ""} onChange={handleChange} className="bg-surface txt-main cursor-pointer">
                                    <option value="" disabled>{t('common.select', 'Selecione...')}</option>
                                    {NATIONALITIES.map((nat) => <option key={nat} value={nat}>{nat}</option>)}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label className="txt-subtle fw-semibold" style={{ fontSize: '0.85rem' }}>{t('components.PersonalArea.emergency_number', 'Emergency Number')}</Form.Label>
                                <PhoneInput
                                    international
                                    defaultCountry="PT"
                                    value={formData.emergency_contact_phone || ""}
                                    onChange={(val) => handlePhoneChange(val, 'emergency_contact_phone')}
                                    className="form-control bg-surface txt-main"
                                    numberInputProps={{ className: "border-0" }}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                </Form>
            </div>

            <div className="mb-5">
                <h3 className="txt-main fw-bold mb-1">{t('components.PersonalArea.bank_info')}</h3>
                <p className="txt-subtle mb-4" style={{ fontSize: '0.9rem' }}>{t('components.PersonalArea.bank_info_desc')}</p>
                <Form>
                    <Row className="mb-3 g-4">
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label className="txt-subtle fw-semibold" style={{ fontSize: '0.85rem' }}>{t('components.PersonalArea.account_format')}</Form.Label>
                                <Form.Control type="text" value="IBAN" readOnly className="bg-surface txt-subtle" disabled />
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label className="txt-subtle fw-semibold" style={{ fontSize: '0.85rem' }}>{t('components.PersonalArea.iban')}</Form.Label>
                                <Form.Control type="text" name="iban" value={formData.iban || ""} readOnly className="bg-surface txt-subtle" disabled />
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label className="txt-subtle fw-semibold" style={{ fontSize: '0.85rem' }}>{t('components.PersonalArea.swift_bic')}</Form.Label>
                                <Form.Control type="text" name="swift_bic" value={formData.swift_bic || ""} readOnly className="bg-surface txt-subtle" disabled />
                            </Form.Group>
                        </Col>
                    </Row>
                </Form>
            </div>

            <div className="mb-5">
                <h3 className="txt-main fw-bold mb-1">{t('components.PersonalArea.id_info')}</h3>
                <p className="txt-subtle mb-4" style={{ fontSize: '0.9rem' }}>{t('components.PersonalArea.id_info_desc')}</p>
                <Form>
                    <Row className="mb-3 g-4">
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label className="txt-subtle fw-semibold" style={{ fontSize: '0.85rem' }}>{t('components.PersonalArea.id_number')}</Form.Label>
                                <Form.Control type="text" name="cc" value={formData.cc || ""} readOnly className="bg-surface txt-subtle" disabled />
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label className="txt-subtle fw-semibold" style={{ fontSize: '0.85rem' }}>{t('components.PersonalArea.ssn')}</Form.Label>
                                <Form.Control type="text" name="social_security_number" value={formData.social_security_number || ""} readOnly className="bg-surface txt-subtle" disabled />
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label className="txt-subtle fw-semibold" style={{ fontSize: '0.85rem' }}>{t('components.PersonalArea.nif')}</Form.Label>
                                <Form.Control type="text" name="nif" value={formData.nif || ""} readOnly className="bg-surface txt-subtle" disabled />
                            </Form.Group>
                        </Col>
                    </Row>
                </Form>
            </div>

            <div className="mb-5">
                <h3 className="txt-main fw-bold mb-1">{t('components.PersonalArea.address_info')}</h3>
                <p className="txt-subtle mb-4" style={{ fontSize: '0.9rem' }}>{t('components.PersonalArea.address_info_desc')}</p>
                <Form>
                    <Row className="mb-3 g-4">
                        <Col md={12}>
                            <Form.Group>
                                <Form.Label className="txt-subtle fw-semibold" style={{ fontSize: '0.85rem' }}>{t('components.PersonalArea.address_line')}</Form.Label>
                                <Form.Control type="text" name="address" value={formData.address || ""} onChange={handleChange} className="bg-surface txt-main" />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row className="mb-3 g-4">
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label className="txt-subtle fw-semibold" style={{ fontSize: '0.85rem' }}>{t('components.PersonalArea.postal_code')}</Form.Label>
                                <Form.Control type="text" name="postal_code" value={formData.postal_code || ""} onChange={handleChange} className="bg-surface txt-main" />
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label className="txt-subtle fw-semibold" style={{ fontSize: '0.85rem' }}>{t('components.PersonalArea.city')}</Form.Label>
                                <Form.Control type="text" name="city" value={formData.city || ""} onChange={handleChange} className="bg-surface txt-main" />
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label className="txt-subtle fw-semibold" style={{ fontSize: '0.85rem' }}>{t('components.PersonalArea.region')}</Form.Label>
                                <Form.Control type="text" name="region" value={formData.region || ""} onChange={handleChange} className="bg-surface txt-main" />
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label className="txt-subtle fw-semibold" style={{ fontSize: '0.85rem' }}>{t('components.PersonalArea.country')}</Form.Label>
                                <Form.Select
                                    name="country"
                                    value={formData.country || ""}
                                    onChange={handleChange}
                                    className="bg-surface txt-main cursor-pointer"
                                >
                                    <option value="" disabled>{t('common.select', 'Selecione...')}</option>
                                    {COUNTRIES.map((country) => (
                                        <option key={country} value={country}>{country}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col className="d-flex justify-content-end mt-4">
                            <Button
                                type="button"
                                variant="primary"
                                className="rounded-pill"
                                onClick={handleSave}
                                disabled={isSaving || !!phoneError || !!dateOfBirthError}
                                style={{ minWidth: '100px', minHeight: '40px', margin: 'auto', padding: '10px 20px' }}
                            >
                                {isSaving ? (
                                    <>
                                        <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                                        {t('common.saving', 'A guardar...')}
                                    </>
                                ) : (
                                    t('common.save_changes', 'Guardar Alterações')
                                )}
                            </Button>
                        </Col>
                    </Row>
                </Form>
            </div>
        </div>
    );
}

export default PersonalArea;